import React from 'react'
import * as Yup from 'yup'
import Wizard, { WizardStep } from 'src/components/forms/Wizard'
import CreateFormSilkRoadConfiguration from './CreateFormSilkRoadConfiguration'

import {
  updateMfSilkRoadConfiguration,
  createMfSilkRoadConfiguration,
} from 'src/services/requests/silkRoadConfiguration'

export default function FormSilkRoadConfiguration({
  editMode = false,
  readOnly = false,
  rowData,
  setOffcanvasVisible = () => {},
  setDeactivateOffcanvasVisible = () => {},
  setExistingStepNumber = () => {},
  initialValuesChanged = false,
  setInitialvaluesChanged = () => {},
  unsavedChangesOffcanvasVisible,
  setUnsavedChangesOffcanvasVisible = () => {},
  fetchData = () => {},
  mfPartners = [],
  t,
}) {
  const initialValues = {
    name: rowData?.partner_id,
    delivery_method: rowData?.delivery_method || '',
    email: rowData?.delivery_method_configuration.email || '',
    language: rowData?.delivery_method_configuration.language || '',
    bcc: rowData?.delivery_method_configuration.bcc || '',
    gln: rowData?.delivery_method_configuration.gln || '',
    sftp_url: rowData?.delivery_method_configuration.sftp_url || '',
    sftp_username: rowData?.delivery_method_configuration.sftp_username || '',
    sftp_password: rowData?.delivery_method_configuration.sftp_password || '',
    sftp_path: rowData?.delivery_method_configuration.sftp_path || '',
    connector_id: rowData?.delivery_method_configuration.connector_id || '',
    status: rowData?.status || 'active',
  }
  const allowedLanguages = ['en', 'de', 'fr', 'it']

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('Yup.validation.error.required')),
    delivery_method: Yup.string().required(t('Yup.validation.error.required')),
    email: Yup.string().email(t('Yup.validation.error.invalid.email')),
    language: Yup.string()
      .notRequired()
      .nullable()
      .oneOf(
        [...allowedLanguages, null],
        'Invalid language code. If provided, it must be one of: en, de, fr, it',
      ),
    bcc: Yup.string(),
    gln: Yup.string(),
    sftp_url: Yup.string(),
    sftp_username: Yup.string(),
    sftp_password: Yup.string(),
    sftp_path: Yup.string().test(
      'starts-ends-with-slash',
      'The path must start and end with a forward slash (/) or be exactly "/".',
      (value) => {
        if (!value) return true
        if (value === '/') return true
        if (value.length < 2) return false
        const startsWithSlash = value.startsWith('/')
        const endsWithSlash = value.endsWith('/')
        return startsWithSlash && endsWithSlash
      },
    ),
    connector_id: Yup.string(),
    status: Yup.string().required('required'),
  })

  const formSchema = {
    step: 'mfSilkRoadConfiguration',
    fields: [
      'name',
      'delivery_method',
      'email',
      'language',
      'bcc',
      'gln',
      'sftp_url',
      'sftp_username',
      'sftp_password',
      'sftp_path',
      'connector_id',
      'status',
    ],
  }

  const handleSubmit = (values) => {
    if (editMode) {
      const body = {
        delivery_method: values.delivery_method,
        delivery_method_configuration: {
          email: values.delivery_method === 'mail' ? values.email : undefined,
          language: values.delivery_method === 'mail' ? values.language : undefined,
          bcc: values.delivery_method === 'mail' ? values.bcc : undefined,
          gln:
            values.delivery_method === 'sftp' || values.delivery_method === 'as2'
              ? values.gln
              : undefined,
          sftp_url: values.delivery_method === 'sftp' ? values.sftp_url : undefined,
          sftp_username: values.delivery_method === 'sftp' ? values.sftp_username : undefined,
          sftp_password: values.delivery_method === 'sftp' ? values.sftp_password : undefined,
          sftp_path: values.delivery_method === 'sftp' ? values.sftp_path : undefined,
          connector_id: values.delivery_method === 'as2' ? values.connector_id : undefined,
        },
        status: values.status,
      }
      updateMfSilkRoadConfiguration(rowData.id, JSON.stringify(body))
        .catch((err) => {
          console.log(err)
        })
        .finally(() => {
          fetchData()
          setOffcanvasVisible(false)
        })
    } else {
      const body = {
        partner_id: values.name,
        delivery_method: values.delivery_method,
        delivery_method_configuration: {
          email: values.delivery_method === 'mail' ? values.email : undefined,
          language: values.delivery_method === 'mail' ? values.language : undefined,
          bcc: values.delivery_method === 'mail' ? values.bcc : undefined,
          gln:
            values.delivery_method === 'sftp' || values.delivery_method === 'as2'
              ? values.gln
              : undefined,
          sftp_url: values.delivery_method === 'sftp' ? values.sftp_url : undefined,
          sftp_username: values.delivery_method === 'sftp' ? values.sftp_username : undefined,
          sftp_password: values.delivery_method === 'sftp' ? values.sftp_password : undefined,
          sftp_path: values.delivery_method === 'sftp' ? values.sftp_path : undefined,
          connector_id: values.delivery_method === 'as2' ? values.connector_id : undefined,
        },
        status: values.status || 'active',
      }
      createMfSilkRoadConfiguration(JSON.stringify(body))
        .catch((err) => {
          console.log(err)
        })
        .finally(() => {
          fetchData()
          setOffcanvasVisible(false)
        })
    }
  }

  return (
    <Wizard
      editMode={editMode}
      readOnly={readOnly}
      rowData={rowData}
      initialValues={initialValues}
      setOffcanvasVisible={setOffcanvasVisible}
      setDeactivateOffcanvasVisible={setDeactivateOffcanvasVisible}
      setInitialvaluesChanged={setInitialvaluesChanged}
      initialValuesChanged={initialValuesChanged}
      unsavedChangesOffcanvasVisible={unsavedChangesOffcanvasVisible}
      setUnsavedChangesOffcanvasVisible={setUnsavedChangesOffcanvasVisible}
      validationSchema={validationSchema}
      setExistingStepNumber={setExistingStepNumber}
      allFormSchema={formSchema}
      onSubmit={(values) => handleSubmit(values)}
      identifier={'mfSilkRoadConfiguration'}
    >
      <WizardStep>
        <CreateFormSilkRoadConfiguration
          editMode={editMode}
          rowData={rowData}
          fetchData={fetchData}
          setOffcanvasVisible={setOffcanvasVisible}
          readOnly={readOnly}
          setExistingStepNumber={setExistingStepNumber}
          mfPartners={mfPartners}
          t={t}
        />
      </WizardStep>
    </Wizard>
  )
}
